// Internationalization utilities for worldwide operation
import { SUPPORTED_COUNTRIES, getCurrencyByCode, getCountryByCode } from '@/constants/locations';

export interface LocalizationConfig {
    locale: string;
    currency: string;
    timezone: string;
    dateFormat: string;
    phonePattern?: RegExp;
    phoneExample?: string;
}

// Get user's locale and currency preferences
export const getUserLocalization = (): LocalizationConfig => {
    // Try to detect from browser
    const browserLocale = navigator.language || 'en-US';
    const countryCode = browserLocale.split('-')[1] || 'US';

    // Find matching country
    const country = getCountryByCode(countryCode.toUpperCase()) || SUPPORTED_COUNTRIES[0];

    return {
        locale: country.locale,
        currency: country.currency,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: getDateFormatForLocale(country.locale),
    };
};

// Get date format based on locale
export const getDateFormatForLocale = (locale: string): string => {
    const formats: Record<string, string> = {
        'en-US': 'MM/dd/yyyy',
        'en-GB': 'dd/MM/yyyy',
        'en-CA': 'yyyy-MM-dd',
        'en-AU': 'dd/MM/yyyy',
        'de-DE': 'dd.MM.yyyy',
        'fr-FR': 'dd/MM/yyyy',
        'es-ES': 'dd/MM/yyyy',
        'it-IT': 'dd/MM/yyyy',
        'ja-JP': 'yyyy/MM/dd',
        'ko-KR': 'yyyy. MM. dd',
        'zh-CN': 'yyyy/MM/dd',
        'ar-SA': 'dd/MM/yyyy',
    };

    return formats[locale] || 'dd/MM/yyyy';
};

// Phone validation patterns by country
export const PHONE_PATTERNS: Record<string, { pattern: RegExp; example: string }> = {
    // North America
    'US': { pattern: /^\+?1?[2-9]\d{2}[2-9]\d{2}\d{4}$/, example: '+1 555 123 4567' },
    'CA': { pattern: /^\+?1?[2-9]\d{2}[2-9]\d{2}\d{4}$/, example: '+1 416 123 4567' },
    'MX': { pattern: /^\+?52?1?\d{10}$/, example: '+52 55 1234 5678' },

    // Europe
    'GB': { pattern: /^\+?44\d{10}$/, example: '+44 20 7946 0958' },
    'DE': { pattern: /^\+?49\d{10,11}$/, example: '+49 30 12345678' },
    'FR': { pattern: /^\+?33\d{9}$/, example: '+33 1 23 45 67 89' },
    'IT': { pattern: /^\+?39\d{6,10}$/, example: '+39 06 12345678' },
    'ES': { pattern: /^\+?34\d{9}$/, example: '+34 91 123 45 67' },
    'NL': { pattern: /^\+?31\d{9}$/, example: '+31 20 123 4567' },
    'CH': { pattern: /^\+?41\d{9}$/, example: '+41 44 123 45 67' },
    'SE': { pattern: /^\+?46\d{8,9}$/, example: '+46 8 123 456 78' },
    'NO': { pattern: /^\+?47\d{8}$/, example: '+47 22 12 34 56' },
    'DK': { pattern: /^\+?45\d{8}$/, example: '+45 32 12 34 56' },

    // Asia
    'JP': { pattern: /^\+?81\d{10,11}$/, example: '+81 3 1234 5678' },
    'CN': { pattern: /^\+?86\d{11}$/, example: '+86 138 0013 8000' },
    'IN': { pattern: /^\+?91\d{10}$/, example: '+91 98765 43210' },
    'KR': { pattern: /^\+?82\d{9,10}$/, example: '+82 10 1234 5678' },
    'SG': { pattern: /^\+?65\d{8}$/, example: '+65 9123 4567' },
    'HK': { pattern: /^\+?852\d{8}$/, example: '+852 9123 4567' },
    'MY': { pattern: /^\+?60\d{9,10}$/, example: '+60 12 345 6789' },
    'TH': { pattern: /^\+?66\d{9}$/, example: '+66 81 234 5678' },
    'VN': { pattern: /^\+?84\d{9}$/, example: '+84 91 234 5678' },
    'PH': { pattern: /^\+?63\d{10}$/, example: '+63 917 123 4567' },
    'ID': { pattern: /^\+?62\d{9,12}$/, example: '+62 812 3456 789' },

    // Middle East
    'IL': { pattern: /^\+?972\d{8,9}$/, example: '+972 50 123 4567' },
    'AE': { pattern: /^\+?971\d{8,9}$/, example: '+971 50 123 4567' },
    'SA': { pattern: /^\+?966\d{8,9}$/, example: '+966 50 123 4567' },
    'QA': { pattern: /^\+?974\d{8}$/, example: '+974 5512 3456' },

    // Africa
    'ZA': { pattern: /^\+?27\d{9}$/, example: '+27 82 123 4567' },
    'NG': { pattern: /^\+?234\d{10}$/, example: '+234 802 123 4567' },
    'GH': { pattern: /^\+?233\d{9}$/, example: '+233 24 123 4567' },
    'KE': { pattern: /^\+?254\d{9}$/, example: '+254 712 123456' },
    'EG': { pattern: /^\+?20\d{10}$/, example: '+20 100 123 4567' },

    // Oceania
    'AU': { pattern: /^\+?61\d{9}$/, example: '+61 412 345 678' },
    'NZ': { pattern: /^\+?64\d{8,9}$/, example: '+64 21 123 4567' },

    // South America
    'BR': { pattern: /^\+?55\d{10,11}$/, example: '+55 11 98765 4321' },
    'AR': { pattern: /^\+?54\d{10}$/, example: '+54 11 1234 5678' },
    'CL': { pattern: /^\+?56\d{8}$/, example: '+56 9 8765 4321' },
    'CO': { pattern: /^\+?57\d{10}$/, example: '+57 300 123 4567' },
};

// Get phone validation for country
export const getPhoneValidation = (countryCode: string) => {
    return PHONE_PATTERNS[countryCode.toUpperCase()] || {
        pattern: /^\+?[\d\s\-\(\)]{7,15}$/,
        example: '+1 234 567 8900'
    };
};

// Validate phone number for specific country
export const validatePhoneNumber = (phone: string, countryCode: string): boolean => {
    const validation = getPhoneValidation(countryCode);
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    return validation.pattern.test(cleanPhone);
};

// Format phone number for display
export const formatPhoneNumber = (phone: string, countryCode: string): string => {
    if (!phone) return '';

    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

    // Basic international formatting
    if (cleanPhone.startsWith('+')) {
        return cleanPhone.replace(/(\+\d{1,4})(\d{2,3})(\d{3,4})(\d{4})/, '$1 $2 $3 $4');
    }

    // Country-specific formatting
    switch (countryCode.toUpperCase()) {
        case 'US':
        case 'CA':
            if (cleanPhone.length === 10) {
                return cleanPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
            }
            break;
        case 'GB':
            if (cleanPhone.length === 11) {
                return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '$1 $2 $3');
            }
            break;
        case 'GH':
            if (cleanPhone.startsWith('233')) {
                return cleanPhone.replace(/(\d{3})(\d{2})(\d{3})(\d{4})/, '+$1 $2 $3 $4');
            } else if (cleanPhone.startsWith('0')) {
                return cleanPhone.replace(/(\d{3})(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4');
            }
            break;
        default:
            // Generic formatting for other countries
            if (cleanPhone.length >= 10) {
                return cleanPhone.replace(/(\d{2,4})(\d{3,4})(\d{4})/, '$1 $2 $3');
            }
    }

    return phone;
};

// Get currencies commonly used in a region
export const getRegionalCurrencies = (countryCode: string): string[] => {
    const regionalCurrencies: Record<string, string[]> = {
        // North America
        'US': ['USD', 'CAD', 'MXN'],
        'CA': ['CAD', 'USD'],
        'MX': ['MXN', 'USD'],

        // Europe (Euro zone and neighbors)
        'DE': ['EUR', 'USD', 'GBP', 'CHF'],
        'FR': ['EUR', 'USD', 'GBP', 'CHF'],
        'IT': ['EUR', 'USD', 'GBP'],
        'ES': ['EUR', 'USD', 'GBP'],
        'GB': ['GBP', 'EUR', 'USD'],
        'CH': ['CHF', 'EUR', 'USD'],
        'SE': ['SEK', 'EUR', 'USD'],
        'NO': ['NOK', 'EUR', 'USD'],
        'DK': ['DKK', 'EUR', 'USD'],

        // Asia
        'JP': ['JPY', 'USD'],
        'CN': ['CNY', 'USD'],
        'IN': ['INR', 'USD'],
        'SG': ['SGD', 'USD'],
        'HK': ['HKD', 'USD'],

        // Middle East
        'AE': ['AED', 'USD'],
        'SA': ['SAR', 'USD'],
        'IL': ['ILS', 'USD', 'EUR'],

        // Africa
        'ZA': ['ZAR', 'USD'],
        'NG': ['NGN', 'USD'],
        'GH': ['GHS', 'USD'],
        'KE': ['KES', 'USD'],
        'EG': ['EGP', 'USD'],

        // Oceania
        'AU': ['AUD', 'USD'],
        'NZ': ['NZD', 'USD', 'AUD'],

        // South America
        'BR': ['BRL', 'USD'],
        'AR': ['ARS', 'USD'],
        'CL': ['CLP', 'USD'],
    };

    return regionalCurrencies[countryCode.toUpperCase()] || ['USD', 'EUR', 'GBP'];
};

// Time zone utilities
export const getCommonTimeZones = () => [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Mexico_City',
    'America/Sao_Paulo',
    'America/Buenos_Aires',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Rome',
    'Europe/Madrid',
    'Europe/Amsterdam',
    'Europe/Stockholm',
    'Europe/Zurich',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Hong_Kong',
    'Asia/Singapore',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Seoul',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Pacific/Auckland',
    'Africa/Lagos',
    'Africa/Cairo',
    'Africa/Johannesburg',
    'Africa/Accra',
    'Africa/Nairobi',
];

// Get appropriate timezone for country
export const getDefaultTimeZone = (countryCode: string): string => {
    const timeZones: Record<string, string> = {
        'US': 'America/New_York',
        'CA': 'America/Toronto',
        'MX': 'America/Mexico_City',
        'GB': 'Europe/London',
        'DE': 'Europe/Berlin',
        'FR': 'Europe/Paris',
        'IT': 'Europe/Rome',
        'ES': 'Europe/Madrid',
        'NL': 'Europe/Amsterdam',
        'CH': 'Europe/Zurich',
        'SE': 'Europe/Stockholm',
        'JP': 'Asia/Tokyo',
        'CN': 'Asia/Shanghai',
        'IN': 'Asia/Kolkata',
        'SG': 'Asia/Singapore',
        'HK': 'Asia/Hong_Kong',
        'AU': 'Australia/Sydney',
        'NZ': 'Pacific/Auckland',
        'ZA': 'Africa/Johannesburg',
        'NG': 'Africa/Lagos',
        'GH': 'Africa/Accra',
        'KE': 'Africa/Nairobi',
        'EG': 'Africa/Cairo',
        'BR': 'America/Sao_Paulo',
        'AR': 'America/Buenos_Aires',
        'AE': 'Asia/Dubai',
        'SA': 'Asia/Riyadh',
        'KR': 'Asia/Seoul',
    };

    return timeZones[countryCode.toUpperCase()] || 'UTC';
};

// Get currency symbol fallback function
export const getCurrencySymbol = (currencyCode: string): string => {
    const currency = getCurrencyByCode(currencyCode);
    if (currency) {
        return currency.symbol;
    }

    // Fallback symbols for common currencies
    const fallbackSymbols: Record<string, string> = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'CNY': '¥',
        'GHS': '₵',
        'INR': '₹',
        'KRW': '₩',
        'RUB': '₽',
        'BRL': 'R$',
        'CAD': 'C$',
        'AUD': 'A$',
        'CHF': 'Fr',
        'SEK': 'kr',
        'NOK': 'kr',
        'DKK': 'kr',
        'PLN': 'zł',
        'CZK': 'Kč',
        'HUF': 'Ft',
        'TRY': '₺',
        'ZAR': 'R',
        'MXN': '$',
        'SGD': 'S$',
        'HKD': 'HK$',
        'NZD': 'NZ$',
        'THB': '฿',
        'MYR': 'RM',
        'PHP': '₱',
        'IDR': 'Rp',
        'VND': '₫',
        'TWD': 'NT$',
        'ILS': '₪',
        'AED': 'د.إ',
        'SAR': 'ر.س',
        'QAR': 'ر.ق',
        'EGP': '£',
        'NGN': '₦',
        'KES': 'KSh',
        'ARS': '$',
        'CLP': '$',
        'COP': '$',
        'PEN': 'S/',
        'UYU': '$U',
    };

    return fallbackSymbols[currencyCode.toUpperCase()] || currencyCode;
};